import { clearMoneyMask } from 'helpers/functions';
import { HIGH_TICKET_PRODUCTS_URL } from 'settings';
import { EChargeType } from 'v2/interfaces/product';
import productService from 'v2/services/product';

const HIGH_TICKET_PRODUCT_EDIT_PATH = '/products/edit';

export const HIGH_TICKET_MIN_PRICE = 7000;

type OfferPrice = {
  value: number;
  currency_code: string;
};

type ProductSaleFields = {
  charge_type: string;
  price?: string | number;
  recurring_value?: string | number;
  accepted_highticket_terms?: boolean;
};

function getNumericPrice(value: string | number | undefined): number {
  return Number(clearMoneyMask(value ?? 0)) || 0;
}

export function isHighTicketValue(value: number | undefined): boolean {
  return (value ?? 0) >= HIGH_TICKET_MIN_PRICE;
}

export function getProductSaleValue(product: ProductSaleFields): number {
  if (product.charge_type === EChargeType.SUBSCRIPTION) {
    return getNumericPrice(product.recurring_value);
  }

  if (product.charge_type === EChargeType.SINGLE) {
    return getNumericPrice(product.price);
  }

  return 0;
}

export function isHighTicketProduct(product: ProductSaleFields): boolean {
  return isHighTicketValue(getProductSaleValue(product));
}

export function shouldDisplayHighTicketTerms(product: ProductSaleFields): boolean {
  return isHighTicketProduct(product) && !product.accepted_highticket_terms;
}

export function isHighTicketOffer(prices: OfferPrice[]): boolean {
  const brlPrice = prices.find(price => price.currency_code === 'BRL')?.value;
  return isHighTicketValue(brlPrice);
}

type DefaultOffer = {
  is_default: boolean;
  is_active: boolean;
  prices?: Array<{ value: number; currency?: { code?: string } }>;
};

export function pickDefaultOffer<T extends DefaultOffer>(offers: T[]): T | undefined {
  return offers.find(offer => offer.is_default) ?? offers.find(offer => offer.is_active);
}

export function getStoredOfferBrlValue(offer?: DefaultOffer): number | undefined {
  return offer?.prices?.find(price => price.currency?.code === 'BRL')?.value;
}

export async function loadActiveCoproducers(productId: number) {
  const coproducers = await productService.getCoproducers(productId).toPromise();
  return coproducers?.active ?? [];
}

export async function loadDefaultHighTicketOffer(productId: number, fallbackValue?: number) {
  const offers = await productService.getOffers(productId);
  const offer = pickDefaultOffer(offers);

  return {
    offerId: offer?.id,
    offerValue: getStoredOfferBrlValue(offer) ?? fallbackValue
  };
}

export type HighTicketPaymentType = 'full' | 'entry_balance';

export const HIGH_TICKET_PAYMENT_TYPE_LABEL: Record<HighTicketPaymentType, string> = {
  full: 'Pagamento total',
  entry_balance: 'Entrada + saldo'
};

type HighTicketListItem = {
  payment_type?: HighTicketPaymentType;
};

export function getHighTicketPaymentType(product: HighTicketListItem): HighTicketPaymentType {
  return product.payment_type === 'entry_balance' ? 'entry_balance' : 'full';
}

export function isEntryBalanceOffer(product: HighTicketListItem): boolean {
  return getHighTicketPaymentType(product) === 'entry_balance';
}

export function getHighTicketPaymentTypeLabel(product: HighTicketListItem): string {
  return HIGH_TICKET_PAYMENT_TYPE_LABEL[getHighTicketPaymentType(product)];
}

export function getHighTicketProductEditUrl(productId: string | number): string {
  return `${HIGH_TICKET_PRODUCTS_URL}${HIGH_TICKET_PRODUCT_EDIT_PATH}/${productId}`;
}
